/**
 * Audit + Compliance Center client.
 *
 * Replaces the previous stub, which resolved to `not_yet_available` because the
 * audit WRITE path existed server-side with no customer-facing read API. That
 * read API now exists; this module is its typed contract.
 *
 * Everything here needs `audit:read` (granted to `org_admin` only). The access
 * evidence endpoint additionally needs `user:read` — an audit permission is not
 * a way around the permission that governs reading people.
 *
 * IMPORTANT: the shapes below are ALREADY PROJECTED. The server decides what a
 * viewer may be told, so there is no raw `detail` bag in any of these types and
 * no way for a client to ask for one.
 */
import { download, get } from './transport.js';
// --- Calls ------------------------------------------------------------------
/** `GET /audit/events` — keyset-paginated activity, newest first. */
export async function events(query = {}, options) {
    return get('/audit/events', { ...options, query: { ...query } });
}
/** `GET /audit/events/:id`. 404 for an event outside the caller's tenant. */
export async function event(id, options) {
    const body = await get(`/audit/events/${id}`, options);
    return body.event;
}
/** `GET /audit/filters` — options derived from the events that exist. */
export async function filters(window = {}, options) {
    return get('/audit/filters', { ...options, query: { ...window } });
}
/**
 * The PATH of a CSV export for the given scope — filters and projection
 * identical to the list.
 *
 * This is a URL BUILDER, not a request binding, and that distinction is the
 * whole reason `exportCsv` below exists. For as long as the Audit Log
 * downloaded through a bare `<a href download>` built from this function, the
 * capability had no request binding anywhere — so the parity census's reach
 * scan, which starts from typed request bindings, could not see the screen's
 * export at all and recorded `audit.audit-export.csv` as a BACKEND-ONLY GAP
 * that a person could in fact click. It is kept because the export's scope is
 * one query shape and `exportCsv` composes it rather than restating it.
 */
export function exportUrl(query = {}) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null || value === '')
            continue;
        params.append(key, String(value));
    }
    const qs = params.toString();
    return `/audit/export.csv${qs ? `?${qs}` : ''}`;
}
/**
 * `GET /audit/export.csv` — the same scope as {@link exportUrl}, FETCHED.
 *
 * A bare anchor download cannot read a response, so it cannot tell a compliance
 * reader that the export was capped, and — the part that actually costs
 * something — it cannot tell them the request FAILED. A 403, a 500 or a dropped
 * connection navigates the browser to an error body and leaves the screen it
 * came from saying nothing at all, which is indistinguishable from a successful
 * export the operator has not found in their Downloads folder yet.
 *
 * So the file comes back through `download()`, which throws a typed error on
 * every non-2xx and reads `x-export-truncated` — the header the audit route
 * emits beside its own `x-audit-truncated` for exactly this reader. The caller
 * hands the text to `downloadTextFile` and every sentence about the file is
 * said after the browser has it.
 *
 * Needs `audit:read`, the SAME key the list read takes and the only key the
 * route's guard checks. There is deliberately no `reporting:export` in this
 * path: that is the admin-views exports' key, and asking for it here would gate
 * a compliance extraction on a permission the server does not check.
 */
export async function exportCsv(query = {}, options) {
    const result = await download(exportUrl(query), options);
    return {
        text: result.text,
        filename: result.filename,
        truncated: result.truncated,
        rowCount: result.rowCount,
    };
}
/** `GET /audit/compliance/overview` — configured controls + recorded evidence. */
export async function complianceOverview(query = {}, options) {
    return get('/audit/compliance/overview', { ...options, query: { ...query } });
}
/** `GET /audit/compliance/access` — needs `audit:read` AND `user:read`. */
export async function accessEvidence(options) {
    return get('/audit/compliance/access', options);
}
//# sourceMappingURL=audit.js.map